// §19.15 / item D.2 / G.2 — Configure modal mounted from the Accept-card's
// integration row. Lets the user provide the actual OpenAPI spec (or
// MCP server URL) the Super Agent flagged a need for, registers it via
// the existing /api/openapi-integrations or /api/mcp/servers route,
// and returns the new integration_id to the parent card so the
// expert-binding can be threaded through provision_agent_tree on Accept.
//
// Reuses the same backend endpoints the Admin → OpenAPI Integrations
// tab uses; doesn't fork the UI. The user pastes their actual spec —
// we never fabricate. Per §19.15 architectural intent: platform-owned
// knowledge (agent shape) is platform-generated; user-owned knowledge
// (vendor specs) is user-provided.

import React, { useState, useEffect } from "react";
import { COLORS } from "../../theme/colors";
import { openapiApi, mcpApi } from "../../api/client";


// Exported for unit-testing: slug derivation from a free-text name.
export function _slugify(name) {
  return (name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "integration";
}

// Exported for unit-testing: drop deleted / quarantined / archived rows so
// users never bind to a broken integration in the existing-integration
// picker. Mirrors the inline filter used on modal open.
export function filterLiveIntegrations(items = []) {
  return items.filter((it) => {
    const s = String(it.status || "active").toLowerCase();
    return s !== "deleted" && s !== "quarantined" && s !== "archived";
  });
}


export default function ConfigureIntegrationModal({
  open, onClose,
  archetype,         // "openapi" | "mcp"
  suggestedName,     // pre-fill name field
  authHint,          // pre-fill auth_method
  purposeSummary,    // 1-line LLM-supplied role description (display only)
  onRegistered,      // (integration) => void — id, name, archetype
}) {
  const [name, setName] = useState(suggestedName || "");
  const [baseUrl, setBaseUrl] = useState("");
  const [authMethod, setAuthMethod] = useState(authHint || "bearer");
  const [credentialRef, setCredentialRef] = useState("");
  const [specText, setSpecText] = useState("");
  const [mcpUrl, setMcpUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Existing-integration picker. When the tenant already has matching
  // integrations registered (e.g. Slack OpenAPI from a prior agent build,
  // or a ServiceNow MCP server), the user shouldn't have to re-register —
  // they should just bind to the existing one. Fetched on modal open.
  const [existing, setExisting] = useState([]);
  const [pickedExistingId, setPickedExistingId] = useState(null);

  const isOpenApi = archetype === "openapi";

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = isOpenApi ? await openapiApi.list() : await mcpApi.listServers();
        if (cancelled) return;
        const items = res?.data || res || [];
        // Both routes return arrays of {id, name, slug, status?, ...}.
        // Drop deleted / quarantined rows so users don't bind to broken
        // integrations.
        setExisting(filterLiveIntegrations(items));
      } catch {
        if (!cancelled) setExisting([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isOpenApi]);

  if (!open) return null;

  const handleUseExisting = () => {
    const found = existing.find(it => it.id === pickedExistingId);
    if (!found) {
      setErr("Pick an existing integration first");
      return;
    }
    onRegistered && onRegistered({
      id: found.id,
      name: found.name || found.slug || "integration",
      archetype: isOpenApi ? "openapi" : "mcp",
    });
    onClose && onClose();
  };

  const handleSave = async () => {
    setErr(null);
    if (!name.trim()) {
      setErr("Name is required");
      return;
    }
    setBusy(true);
    try {
      if (isOpenApi) {
        if (!baseUrl.trim()) {
          throw new Error("base_url is required for OpenAPI integrations");
        }
        let spec = null;
        const trimmed = specText.trim();
        if (trimmed) {
          // Accept either raw JSON or YAML (server normalises). Try
          // JSON.parse first; if that fails, send as a string and let
          // the backend's preview/parse handle it.
          try { spec = JSON.parse(trimmed); }
          catch { spec = { _raw: trimmed }; }
        }
        const slug = _slugify(name);
        const payload = {
          name: name.trim(),
          slug,
          base_url: baseUrl.trim(),
          auth_method: authMethod,
          credential_ref: credentialRef.trim() || null,
          spec,
        };
        const res = await openapiApi.create(payload);
        if (!res.success || !res.data?.id) {
          throw new Error(res.data?.error || "create failed");
        }
        onRegistered && onRegistered({
          id: res.data.id,
          name: res.data.name || name.trim(),
          archetype: "openapi",
        });
      } else {
        // MCP
        if (!mcpUrl.trim()) {
          throw new Error("MCP server URL is required");
        }
        const payload = {
          name: name.trim(),
          slug: _slugify(name),
          transport: "http",
          url: mcpUrl.trim(),
        };
        const res = await mcpApi.registerServer(payload);
        if (!res.success || !res.data?.id) {
          throw new Error(res.data?.error || "register failed");
        }
        onRegistered && onRegistered({
          id: res.data.id,
          name: res.data.name || name.trim(),
          archetype: "mcp",
        });
      }
      onClose && onClose();
    } catch (e) {
      const detail = e?.body?.detail;
      setErr(typeof detail === "string" ? detail : (detail?.message || e?.message || String(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div style={{
        width: "min(640px, 92vw)",
        maxHeight: "92vh",
        overflowY: "auto",
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        padding: 20,
        boxShadow: "0 12px 36px rgba(0,0,0,0.5)",
      }}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
            Configure {isOpenApi ? "OpenAPI integration" : "MCP server"}
          </div>
          {purposeSummary && (
            <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>
              {purposeSummary}
            </div>
          )}
          <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4, fontStyle: "italic" }}>
            Paste your actual spec — we never fabricate vendor APIs.
          </div>
        </div>

        {/* Use-existing picker — only render if at least one matching
            integration exists in the tenant. The user can either bind to
            one of those or scroll down and register a new one below. */}
        {existing.length > 0 && (
          <div style={{
            marginBottom: 14, padding: 12,
            background: `${COLORS.accent}08`, border: `1px solid ${COLORS.accent}25`,
            borderRadius: 8,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>
              Use an existing {isOpenApi ? "OpenAPI integration" : "MCP server"} ({existing.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 180, overflowY: "auto" }}>
              {existing.map(it => (
                <label key={it.id} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "6px 10px", borderRadius: 6,
                  background: pickedExistingId === it.id ? `${COLORS.accent}18` : "transparent",
                  border: `1px solid ${pickedExistingId === it.id ? COLORS.accent : "transparent"}`,
                  cursor: "pointer", fontSize: 12, color: COLORS.text,
                }}>
                  <input
                    type="radio" name="existing-integration"
                    checked={pickedExistingId === it.id}
                    onChange={() => setPickedExistingId(it.id)}
                  />
                  <span style={{ fontWeight: 600 }}>{it.name || it.slug}</span>
                  {it.slug && it.slug !== it.name && (
                    <span style={{ fontSize: 10, color: COLORS.textDim }}>{it.slug}</span>
                  )}
                  {it.status && (
                    <span style={{
                      marginLeft: "auto", fontSize: 9,
                      color: String(it.status).toLowerCase() === "healthy" || String(it.status).toLowerCase() === "active"
                        ? COLORS.green : COLORS.orange,
                    }}>{it.status}</span>
                  )}
                </label>
              ))}
            </div>
            <button
              type="button"
              onClick={handleUseExisting}
              disabled={!pickedExistingId}
              style={{
                marginTop: 10, padding: "6px 14px", borderRadius: 6, border: "none",
                background: pickedExistingId ? COLORS.accent : COLORS.surfaceHover,
                color: pickedExistingId ? "#fff" : COLORS.textDim,
                fontSize: 12, fontWeight: 600,
                cursor: pickedExistingId ? "pointer" : "not-allowed",
              }}
            >Use selected</button>
            <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 8, fontStyle: "italic" }}>
              Or register a new one below.
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label="Name">
            <input
              type="text" value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. ServiceNow Incidents"
              style={inputStyle}
            />
          </Field>

          {isOpenApi ? (
            <>
              <Field label="Base URL">
                <input
                  type="text" value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://your-instance.service-now.com"
                  style={inputStyle}
                />
              </Field>
              <Field label="Auth method">
                <select
                  value={authMethod}
                  onChange={(e) => setAuthMethod(e.target.value)}
                  style={inputStyle}
                >
                  <option value="none">none (public)</option>
                  <option value="bearer">bearer</option>
                  <option value="basic">basic</option>
                  <option value="api_key_header">api_key_header</option>
                  <option value="api_key_query">api_key_query</option>
                  <option value="oauth2_client_credentials">oauth2_client_credentials</option>
                </select>
              </Field>
              {authMethod !== "none" && (
                <Field label="Credential reference" hint="Dotted path into tenant settings (e.g. openapi.servicenow.bearer_token). Set the value in Admin → API Keys.">
                  <input
                    type="text" value={credentialRef}
                    onChange={(e) => setCredentialRef(e.target.value)}
                    placeholder="openapi.servicenow.bearer_token"
                    style={inputStyle}
                  />
                </Field>
              )}
              <Field label="OpenAPI spec (paste JSON; YAML accepted but JSON safer)">
                <textarea
                  value={specText}
                  onChange={(e) => setSpecText(e.target.value)}
                  placeholder='{"openapi": "3.0.0", "info": {...}, "paths": {...}}'
                  rows={8}
                  style={{ ...inputStyle, fontFamily: "ui-monospace, monospace", fontSize: 11 }}
                />
              </Field>
            </>
          ) : (
            <Field label="MCP server URL" hint="HTTP transport URL the MCP gateway will reach.">
              <input
                type="text" value={mcpUrl}
                onChange={(e) => setMcpUrl(e.target.value)}
                placeholder="https://mcp.your-tenant.example.com"
                style={inputStyle}
              />
            </Field>
          )}
        </div>

        {err && (
          <div style={{
            marginTop: 12, padding: "8px 12px",
            borderRadius: 6,
            background: `${COLORS.pink}15`,
            color: COLORS.pink,
            fontSize: 11,
          }}>{err}</div>
        )}

        <div style={{
          marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end",
          paddingTop: 12, borderTop: `1px solid ${COLORS.border}`,
        }}>
          <button
            type="button"
            onClick={() => !busy && onClose && onClose()}
            disabled={busy}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: `1px solid ${COLORS.border}`,
              background: "transparent",
              color: COLORS.textMuted,
              fontSize: 12, cursor: busy ? "not-allowed" : "pointer",
            }}
          >Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: "none",
              background: busy ? COLORS.surfaceHover : COLORS.accent,
              color: busy ? COLORS.textDim : "#fff",
              fontSize: 12, fontWeight: 600,
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >{busy ? "Registering…" : "Register"}</button>
        </div>
      </div>
    </div>
  );
}


const inputStyle = {
  width: "100%",
  padding: "6px 10px",
  borderRadius: 6,
  border: `1px solid ${COLORS.border}`,
  background: COLORS.bg,
  color: COLORS.text,
  fontSize: 12,
  outline: "none",
  boxSizing: "border-box",
  resize: "vertical",
};


function Field({ label, hint, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>
        {label}
      </div>
      {children}
      {hint && (
        <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 3 }}>{hint}</div>
      )}
    </div>
  );
}
